
import React, { useEffect, useState } from 'react';
import { StyleMode } from '../../../../types';
import { Star, ArrowRight } from 'lucide-react';
import { LABELS } from '../../../../constants/labels';
import { HeroVariantProps, getEffectiveColor, getTextureClass, getOutlineStyle } from '../shared';
import { sanitizeInlineHtml } from '../../../../services/sanitizeHtml';
import { StoreImage } from '../../../ui/StoreImage';

export const HeroCard: React.FC<HeroVariantProps> = (props) => {
    const { theme, student, className, title, text, imageUrl, mobileImageUrl, onScrollToShop, parallaxRef, customButtons, customBadge, heroContentPosition = 50, heroContentPositionY = 50, heroTextAlignment = 'center', heroButtonColor, heroButtonTextColor } = props;
    const styleMode: StyleMode = theme?.styleMode || 'modern';
    const [mounted, setMounted] = useState(false);

    useEffect(() => { setMounted(true); }, []);
    
    const getLayoutSettings = (mode: StyleMode) => {
        switch (mode) {
            case 'bold':
                return {
                    wrapper: 'md:ml-12 lg:ml-20 max-w-xl', 
                    boxStyle: {
                        backgroundColor: theme?.primary || '#1A120B',
                        border: `3px solid ${theme?.accent || '#D8C8C0'}`,
                        boxShadow: '6px 6px 0px rgba(0,0,0,0.4)',
                        borderRadius: '0px',
                    },
                    buttonStyle: 'border-2 border-stone-900 shadow-[3px_3px_0px_white] hover:shadow-[1px_1px_0px_white] hover:translate-x-[1px] hover:translate-y-[1px]',
                    tagStyle: 'bg-black border border-white/20'
                };
            case 'classic':
            case 'soft':
            case 'modern':
            default:
                return {
                    wrapper: 'max-w-3xl', 
                    boxStyle: {
                        backgroundColor: 'var(--glass-bg)',
                        border: '1px solid var(--glass-border)',
                        boxShadow: '0 15px 35px rgba(0, 0, 0, 0.08)',
                        borderRadius: 'var(--radius-card)',
                        backdropFilter: 'blur(var(--glass-blur))',
                        WebkitBackdropFilter: 'blur(var(--glass-blur))'
                    },
                    buttonStyle: 'hover:shadow-xl shadow-lg hover:-translate-y-0.5',
                    tagStyle: 'bg-black/40 backdrop-blur-md border border-white/10 shadow-sm',
                };
        }
    };

    const layout = getLayoutSettings(styleMode);
    const finalTitleColor = getEffectiveColor(props.titleColor, 'var(--hero-color-title)');
    const finalTextColor = getEffectiveColor(props.textColor, 'var(--hero-color-text)');

    const btnStyle = {
        borderRadius: 'var(--radius-btn, 99px)',
        backgroundColor: heroButtonColor,
        color: heroButtonTextColor
    };

    const isDesktop = window.innerWidth > 768;

    const cssTopPercent = 100 - heroContentPositionY;
    const cssLeftPercent = isDesktop ? heroContentPosition : 50;

    const textureClass = getTextureClass(props.heroTitleTexture);
    
    const titleOutline = getOutlineStyle(props.heroTitleOutlineWidth, props.heroTitleOutlineColor || finalTitleColor);
    const textOutline = getOutlineStyle(props.heroTextOutlineWidth, props.heroTextOutlineColor || finalTextColor);

    const altText = title ? title.replace(/<[^>]*>?/gm, '') : "Kaffeförsäljning till klasskassan";

    return (
        <div className={`relative ${props.heightClass} flex bg-stone-100 overflow-hidden group perspective-1000`}>
            <div 
                ref={parallaxRef}
                    data-parallax
                className="absolute inset-0 w-full h-[120%] -top-[10%] overflow-hidden"
                style={{ transform: 'translate3d(0,0,0)', willChange: 'transform' }}
            >
                <div className={`w-full h-full transition-transform duration-[12000ms] ease-out ${mounted ? 'scale-105' : 'scale-100'}`}>
                    <StoreImage src={imageUrl} mobileSrc={mobileImageUrl || undefined} width={1600} alt={altText} className="w-full h-full object-cover object-center" />
                </div>
            </div>

            <div className="absolute inset-0 px-[6%] py-[6%] pointer-events-none z-10">
                <div className="relative w-full h-full">
                    <div 
                        className={`
                            pointer-events-auto
                            w-full p-6 md:p-10 ${layout.wrapper} transition-all duration-1000 ease-out 
                            flex flex-col relative
                            ${heroTextAlignment === 'center' ? 'items-center' : heroTextAlignment === 'right' ? 'items-end' : 'items-start'}
                            ${mounted ? 'opacity-100' : 'opacity-0 translate-y-6'}
                        `}
                        style={{
                            ...layout.boxStyle as React.CSSProperties,
                            position: 'absolute',
                            left: `${cssLeftPercent}%`,
                            top: `${cssTopPercent}%`,
                            transform: `translate(-${isDesktop ? cssLeftPercent : 50}%, -${cssTopPercent}%)`,
                            textAlign: heroTextAlignment,
                            maxWidth: isDesktop ? '600px' : '100%'
                        }}
                    >
                        {customBadge ? customBadge : (student && (
                            <div 
                                className={`inline-flex items-center gap-2 px-3 py-1 text-white text-[9px] font-bold uppercase tracking-widest mb-4 ${layout.tagStyle}`}
                                style={{ borderRadius: styleMode === 'bold' ? '0px' : '99px' }}
                            >
                                <Star size={10} className="text-yellow-400 fill-yellow-400 animate-pulse" /> 
                                <span>Stöd {student.name} {className ? `& ${className}` : ''}</span>
                            </div>
                        ))}
                        
                        <h1 
                            className={`${props.titleSizeClass} font-heading font-bold mb-4 tracking-tight leading-[1.15] ${styleMode === 'bold' ? 'uppercase font-black' : ''} ${props.titleShadowClass} ${textureClass}`}
                            style={{ 
                                color: textureClass ? 'transparent' : finalTitleColor, 
                                fontFamily: props.theme?.heroTitleFont,
                                filter: textureClass ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : undefined,
                                ...titleOutline
                            }}
                        >
                            {title ? (
                                <span dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(title.replace(/\n/g, '<br/>')) }} />
                            ) : <>Handplockat. <br/> Omsorgsfullt rostat.</>}
                        </h1>
                        
                        <p 
                            className={`${props.subtitleSizeClass} mb-8 max-w-lg leading-relaxed font-body font-light opacity-90 ${props.textShadowClass}`}
                            style={{ 
                                color: finalTextColor, 
                                fontFamily: props.theme?.heroTextFont,
                                ...textOutline
                            }}
                        >
                            {text || `Vårt premiumkaffe är noggrant utvalt och rostat till perfektion. När du handlar här går vinsten oavkortat till ${LABELS.group.definite.toLowerCase()}.`}
                        </p>
                        
                        <div>
                            {customButtons}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
